import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, FlaskConicalIcon, InfoIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Button } from '../ui/Button';
import { useDataMode } from '../../contexts/DataModeContext';
export interface FirstRunStep {
  label: string;
  detail: string;
  to?: string;
}

/**
 * The state a surface is in before the person has done anything on it.
 *
 * It answers three questions in order, because a blank panel answers none of
 * them: what this is, why it is worth your time, and what to do first. The
 * primary step is a real destination, never a decorative call to action.
 */
export function FirstRun({
  surface,
  what,
  why,
  steps,
  note
}: {
  surface: string;
  what: string;
  why: string;
  steps: FirstRunStep[];
  /** Anything the person should know before starting, e.g. what Campuzon cannot do here. */
  note?: string;
}) {
  const primary = steps[0];
  return <div className="space-y-5" data-id="element-3966" data-mp="1jgdttltbpixs">
      <Panel bodyClassName="p-6 sm:p-8" data-id="element-3967" data-mp="124bw6k0057n9">
        <p className="text-caption font-semibold uppercase tracking-wide text-brand-700" data-id="element-3968" data-mp="125den9jckwf0">
          {surface}
        </p>
        <h2 className="mt-2 max-w-[46ch] text-h3 font-semibold text-ink" data-id="element-3969" data-mp="x7293b03582l">{what}</h2>
        <p className="mt-3 max-w-[70ch] text-body-lg text-ink" data-id="element-3970" data-mp="1axffh8xbwept">{why}</p>

        {primary?.to && <div className="mt-5" data-id="element-3971" data-mp="16546lgkm1il4">
            <Link to={primary.to} data-id="element-3972" data-mp="1642qgxoqm5wj">
              <Button data-id="element-3973" data-mp="1ku0m1yozxxvr">
                {primary.label}
                <ArrowRightIcon aria-hidden className="h-4 w-4" data-id="element-3974" data-mp="1kwwgve5js6fz" />
              </Button>
            </Link>
          </div>}

        {note && <p className="mt-5 flex max-w-[72ch] items-start gap-2 border-t border-line pt-4 text-body-sm text-ink-muted" data-id="element-3975" data-mp="1tugmezag0owe">
            <InfoIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-3976" data-mp="1puehmh9evfcw" />
            {note}
          </p>}
      </Panel>

      <Panel title="Where to start" bodyClassName="p-0" data-id="element-3977" data-mp="3voo9tabv67j8">
        <ol className="divide-y divide-line" data-id="element-3978" data-mp="3rmkwsh9jquvu">
          {steps.map((step, index) => <li key={step.label} className="flex items-start gap-4 px-5 py-4 sm:px-6" data-id="element-3979" data-mp="1ulottxsgbdrb" data-mpi={index}>
              <span className="cz-num mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line-strong text-body-sm font-semibold text-ink-muted" data-id="element-3980" data-mp="1zix133c29xds">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1" data-id="element-3981" data-mp="29inzvh7nmyjo">
                <span className="block text-body font-medium text-ink" data-id="element-3982" data-mp="2djhnsal3kdck">{step.label}</span>
                <span className="mt-0.5 block max-w-[70ch] text-body-sm text-ink-muted" data-id="element-3983" data-mp="2dhn758guz7ea">
                  {step.detail}
                </span>
              </span>
              {step.to && <Link to={step.to} className="shrink-0" data-id="element-3984" data-mp="2nbncnbg1yenj">
                  <Button size="sm" variant="ghost" data-id="element-3985" data-mp="2s72nrvmodwg9">
                    Open
                  </Button>
                </Link>}
            </li>)}
        </ol>
      </Panel>

      <DemoDataHint data-id="element-3986" data-mp="319vffzzrsa6m" />
    </div>;
}

/** Offers the way back to seeded records, so the empty state is not a dead end. */
export function DemoDataHint() {
  const {
    setMode
  } = useDataMode();
  return <Panel bodyClassName="p-5" data-id="element-3987" data-mp="ljc2hyipnr5a">
      <p className="flex max-w-[74ch] items-start gap-2 text-body-sm text-ink-muted" data-id="element-3988" data-mp="lm81lq6aolvb">
        <FlaskConicalIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" data-id="element-3989" data-mp="glainbdgnnsr" />
        You are viewing Campuzon as a new account, with nothing on it. To see these surfaces with
        records in them, switch back to the seeded demonstration data — it is clearly labelled as
        demonstration data throughout.
      </p>
      <div className="mt-3" data-id="element-3990" data-mp="14cz7uiuzjw5h">
        <Button size="sm" variant="secondary" onClick={() => setMode('demo')} data-id="element-3991" data-mp="zjczv3zrogg0">
          Show demonstration data
        </Button>
      </div>
    </Panel>;
}